"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebrtcPlayer = void 0;
const mobx_1 = require("mobx");
// eslint-disable-next-line import/no-cycle
const api_1 = require("../../api");
const adapter_1 = require("../../api/adapter");
const feature_1 = require("../../api/adapter/features/feature");
const feature_2 = require("../../api/player/features/feature");
const errors_1 = require("../errors");
const errors_deprecated_1 = require("../errors-deprecated");
const bind_1 = require("../utils/bind");
const browser_1 = require("../utils/browser-support/browser");
const context_1 = require("../utils/context/context");
const event_emitter_1 = require("../utils/events/event-emitter");
const logger_1 = require("../utils/logger");
const sync_handler_1 = require("../utils/proxy/sync-handler");
const stats_1 = require("../utils/stats");
const video_client_1 = require("../video-client");
const core_1 = require("./core");
const helper_1 = require("./helper");
const mediasoup_1 = require("./mediasoup");
class WebrtcPlayer extends event_emitter_1.ObservableEventEmitter {
    attachTo(el) {
        this.hostEl = (0, helper_1.attachVideoElement)(el);
    }
    setPreferredLevel(level) {
        this.preferredLevel = level;
    }
    // broadcast feature
    get canBroadcast() {
        return this.call != null;
    }
    get callBroadcast() {
        return this.call;
    }
    //#endregion
    get currentPlayer() {
        return this[sync_handler_1.implementation];
    }
    set currentPlayer(player) {
        this[sync_handler_1.implementation] = player;
    }
    get logger() {
        return this.ctx.logger;
    }
    static async isSupported(logger) {
        return (0, browser_1.supportsMediasoupWebrtc)("Webrtc", logger);
    }
    isImplements(feature) {
        switch (feature) {
            case feature_2.Feature.MUTED_AUTOPLAY:
            case feature_2.Feature.CONSUMER:
            case feature_2.Feature.BROADCAST:
            case feature_2.Feature.BITRATE_SWITCHING:
                return true;
            default:
                return false;
        }
    }
    static get format() {
        return "webrtc";
    }
    get format() {
        return WebrtcPlayer.format;
    }
    constructor(ctx, provider, options) {
        super(false);
        this.driverFailover = false;
        this.blurred = false;
        this.isConnecting = false;
        this.isConnected = false;
        this.call = null;
        this.manifestJson = null;
        this.videoEl = null;
        this.callStreamName = null;
        this.callId = null;
        this.isJoiningCall = false;
        this.sfuConnectionLoadId = 0;
        this.videoConsumerLoadId = 0;
        this.firstTimeUpdateLoadId = 0;
        this.joinCallEndpointLoadId = stats_1.stats.start(stats_1.STATS_EVENTS.JOIN_CALL_ENDPOINT);
        this.ctx = ctx;
        this.mediaLoader = provider;
        this.options = options;
        this.autoPlay = options.autoPlay != null ? options.autoPlay : true;
        (0, mobx_1.makeObservable)(this, {
            // observables
            isConnected: mobx_1.observable,
            isConnecting: mobx_1.observable,
        });
        (0, context_1.onceCanceled)(ctx).then((reason) => this.dispose(`WebrtcPlayer Context Cancelled: ${reason}`));
        // don't initialize any properties form Proxy region
        // before next line, otherwise they will be shadowed
        const self = (0, bind_1.makeBounded)(new Proxy(this, core_1.proxyHandler));
        // we replaced context so between this moment
        // and the end of the constructor we should use `self`
        // instead of `this`
        self.init();
        ctx.logger.attachObject(self);
        ctx.logger.trace("constructor()", {});
        return self;
    }
    // noinspection JSUnusedLocalSymbols
    init() {
        this.mediaLoader.on("source", this.handleSource);
        this.addInnerDisposer((reason) => {
            this.mediaLoader.off("source", this.handleSource);
        });
        if (this.mediaLoader.source != null) {
            this.handleSource(this.mediaLoader.source);
        }
    }
    async handleSource(manifest) {
        if (this.suspended) {
            return;
        }
        this.ctx.logger.debug("webrtc: handleSource()");
        this.manifestJson = manifest;
        if (manifest == null) {
            this.stopCall();
        }
        else {
            const { webrtc } = manifest.formats;
            if (webrtc == null) {
                this.emitErrorDeprecated(new errors_deprecated_1.NotSupportedError("manifest doesn't contains webrtc format", {}));
                return;
            }
            if (!this.isJoiningCall ||
                this.callId !== webrtc.origin.callId ||
                this.callStreamName !== webrtc.origin.streamNames[0]) {
                this.isJoiningCall = true;
                this.callId = webrtc.origin.callId;
                this.callStreamName = webrtc.origin.streamNames[0];
                this.stopCall();
                await this.joinCall(this.callId, webrtc.origin.streamNames[0], webrtc.origin.token, {
                    rsrc: webrtc.origin.rsrc,
                    xkey: webrtc.origin.publicKey,
                    bpeerId: webrtc.origin.peerId,
                    joinUrl: webrtc.origin.location,
                });
            }
        }
    }
    stopCall() {
        var _a;
        if (this.call != null) {
            (_a = this.call) === null || _a === void 0 ? void 0 : _a.removeAllListeners("playerAdded");
            this.call = null;
        }
    }
    get sfuJoinParams() {
        var _a, _b;
        const { webrtc } = (_b = (_a = this.manifestJson) === null || _a === void 0 ? void 0 : _a.formats) !== null && _b !== void 0 ? _b : {};
        if ((webrtc === null || webrtc === void 0 ? void 0 : webrtc.origin.uri) == null) {
            return null;
        }
        return {
            call: {
                id: webrtc.origin.callId,
                sfu: {
                    uri: webrtc.origin.uri,
                    httpUri: webrtc.origin.httpUri,
                    region: webrtc.origin.region,
                    version: webrtc.origin.version,
                },
                turn: webrtc.origin.turn,
                support: "ovh",
            },
            user: {
                userId: webrtc.origin.token,
                scope: "viewer",
                displayName: "",
                authorizeToken: webrtc.origin.token,
            },
        };
    }
    async joinCall(callId, streamName, token, sfuOptions) {
        var _a, _b, _c, _d, _e;
        const statInfo = {
            driver: this.format,
            abr: (_a = this.manifestJson) === null || _a === void 0 ? void 0 : _a.abr,
            aor: (_b = this.manifestJson) === null || _b === void 0 ? void 0 : _b.aor,
            atr: (_c = this.manifestJson) === null || _c === void 0 ? void 0 : _c.atr,
            rep: (_d = this.manifestJson) === null || _d === void 0 ? void 0 : _d.rep,
        };
        const joinCallOptions = {
            token,
            onCallJoinEndpoint: () => {
                this.emit("joinCallEndpoint", stats_1.stats.stop(this.joinCallEndpointLoadId, statInfo));
                this.sfuConnectionLoadId = stats_1.stats.start(stats_1.STATS_EVENTS.SFU_CONNECTION);
            },
            onSFUConnection: () => {
                this.emit("sfuConnection", stats_1.stats.stop(this.sfuConnectionLoadId, statInfo));
                this.videoConsumerLoadId = stats_1.stats.start(stats_1.STATS_EVENTS.VIDEO_CONSUMER);
            },
            playerOptions: this.options,
        };
        try {
            this.call = await this.ctx.videoClient.internalJoinCall(callId, this.sfuJoinParams, { ...sfuOptions, ...joinCallOptions }, (0, logger_1.extendContext)(this.ctx, video_client_1.VideoClient));
            this.emit("joinedCall", { call: this.call });
            this.call.once("videoConsumer", () => {
                this.emit("videoConsumer", stats_1.stats.stop(this.videoConsumerLoadId, statInfo));
                this.firstTimeUpdateLoadId = stats_1.stats.start(stats_1.STATS_EVENTS.FIRST_TIME_UPDATE);
            });
            this.call.on("playerAdded", (ev) => {
                this.ctx.logger.debug("got a player", { ev });
                if (streamName !== ev.streamName) {
                    return;
                }
                this.ctx.logger.debug("set currentPlayer", { old: this.currentPlayer });
                if (ev.player instanceof mediasoup_1.MediasoupPlayer) {
                    ev.player.isManifestPlayer = true;
                }
                this.currentPlayer = ev.player;
                this.currentPlayer.once("videoFirstPlay", () => {
                    this.emit("firstTimeUpdate", stats_1.stats.stop(this.firstTimeUpdateLoadId, statInfo));
                });
            });
            this.call.on("peerAtCapacity", (ev) => {
                this.emit("peerAtCapacity", ev);
            });
            this.call.on("callEnded", () => {
                this.emit("callEnded");
            });
            this.call.on("isConnected", (ev) => {
                this.isConnected = ev;
            });
            this.call.on("isConnecting", (ev) => {
                this.isConnecting = ev;
            });
            this.call.on("playerRemoved", (ev) => {
                var _a;
                if (streamName !== ev.streamName) {
                    return;
                }
                (_a = this.currentPlayer) === null || _a === void 0 ? void 0 : _a.dispose("peer player removed due to call.on('playerRemoved') event");
            });
            this.call.on("websocketReconnect", () => {
                this.emit("websocketReconnect");
            });
            this.call.on("error", (ev) => {
                this.emitErrorDeprecated(ev);
            });
        }
        catch (err) {
            this.emit("error", (_e = (0, errors_1.wrapError)(err)) !== null && _e !== void 0 ? _e : (0, errors_1.createError)(api_1.ErrorCode.CallError, "unknown error", {}));
        }
    }
    updatePosterWithLatestFrame() {
        if (this.hostEl != null && adapter_1.device.isImplements(feature_1.Feature.CREATE_SCREENSHOT)) {
            this.poster = adapter_1.device.createScreenshot(this.hostEl);
        }
        else {
            this.ctx.logger.warn("Unable to set update poster. Create screenshot is not compatible with device.");
        }
    }
    replaceManifestUrl(url) {
        throw (0, errors_1.createError)(api_1.ErrorCode.NotSupported);
    }
    toJSON() {
        var _a, _b;
        return (_b = (_a = this.currentPlayer) === null || _a === void 0 ? void 0 : _a.toJSON()) !== null && _b !== void 0 ? _b : {};
    }
}
exports.WebrtcPlayer = WebrtcPlayer;
WebrtcPlayer.displayName = "WebrtcPlayer";
//# sourceMappingURL=webrtc.js.map